import { ReadonlyURLSearchParams, usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  REQUIRE_ATTACHMENTS_STATUS,
  REQUIRE_ATTACHMENTS_STATUS_PREVIOUS_CUSTOMER,
  IS_PROCESSING_STATUS,
  FOR_REPAIR_STATUS,
  ADD_INFORMATION_STATUS,
  OFFER_ACTIVE_STATUS,
} from "@/constants/common";
import { SCREENS } from "@/router/screens";
import { getContractUrl } from "@/router";
import { isAssignedToCore } from "@/utils/contract";
import { isContractCheck } from "@/utils/request";
import { contractCanNotBeModifiedContext } from "@/modals/ContractCanNotBeModified";
import { openContextModal } from "@mantine/modals";
import { RequestStatusType } from "@/constants/request";
import { ContractState, Process } from "@/types/contract";
import { useIsNewCustomerLink } from "./useIsNewCustomerLink";
import { useIsOperatorLink } from "./useIsOperatorLink";

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useRedirect = (uuid: string, contractState?: ContractState) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isRequest = pathname.includes("/request");
  const isNewCustomerLink = useIsNewCustomerLink();
  const isOperatorLink = useIsOperatorLink();

  const redirect = (url: string, additionalSearchParams?: Record<string, string | number>) => {
    router.replace(addSearchParams(url, searchParams, additionalSearchParams));
  };

  const additionalDocuments = contractState?.contract.additionalDocuments ?? [];
  const previousCustomerAdditionalDocuments = contractState?.contract.previousCustomerAdditionalDocuments ?? [];

  if (
    [REQUIRE_ATTACHMENTS_STATUS_PREVIOUS_CUSTOMER, REQUIRE_ATTACHMENTS_STATUS].includes(
      contractState?.contract.contractStatus ?? ""
    ) &&
    !onPage([SCREENS.ATTACHMENTS, SCREENS.THANK_YOU], pathname)
  ) {
    redirect(getContractUrl(uuid, SCREENS.ATTACHMENTS, isRequest));
  }

  if (
    onPage([SCREENS.SUPPLY_POINT_CONFIRM, SCREENS.WELCOME], pathname) &&
    isRequest &&
    contractState?.contract.requestStatus === RequestStatusType.ACCEPTED &&
    contractState.contract.relatedContract
  ) {
    redirect(`/contract/${contractState.contract.relatedContract}${SCREENS.WELCOME}`);
  }

  if (
    onPage([SCREENS.WELCOME], pathname) &&
    isRequest &&
    contractState &&
    [RequestStatusType.FINISHED, RequestStatusType.FOR_APPROVAL].includes(contractState.contract.requestStatus ?? "") &&
    contractState.contract.relatedContract &&
    isNewCustomerLink
  ) {
    redirect(`/contract/${contractState.contract.relatedContract}${SCREENS.WELCOME}`);
  }

  if (
    onPage([SCREENS.WELCOME], pathname) &&
    isRequest &&
    contractState &&
    [RequestStatusType.EDITING, RequestStatusType.SAVED, RequestStatusType.EXPIRED_UNFINISHED].includes(
      contractState.contract.requestStatus ?? ""
    ) &&
    isNewCustomerLink
  ) {
    openContextModal({
      ...contractCanNotBeModifiedContext,
    });
  }

  if (
    onPage([SCREENS.WELCOME, SCREENS.SUPPLY_POINT_CONFIRM], pathname) &&
    isRequest &&
    contractState &&
    ([
      RequestStatusType.EXPIRED_SUBMITTED,
      RequestStatusType.CANCELED_BY_NEW_CUSTOMER,
      RequestStatusType.CANCELED_BY_OPERATOR,
      RequestStatusType.CANCELED_BY_OLD_CUSTOMER,
      RequestStatusType.REJECTED,
    ].includes(contractState.contract.requestStatus ?? "") ||
      contractState.contract.gdprAnonymization) &&
    isNewCustomerLink
  ) {
    if (contractState.contract.relatedContract) {
      redirect(`/contract/${contractState.contract.relatedContract}${SCREENS.WELCOME}`);
    }
    if (!contractState.contract.relatedContract) {
      openContextModal({
        ...contractCanNotBeModifiedContext,
      });
    }
  }
  if (
    isAssignedToCore(contractState?.contract) &&
    !pathname.includes(SCREENS.DOWNLOAD_DOCUMENTS) &&
    !pathname.includes(SCREENS.SMS_CODE) &&
    !pathname.includes(SCREENS.THANK_YOU)
  ) {
    redirect(getContractUrl(uuid, SCREENS.DOWNLOAD_DOCUMENTS, isRequest));
  }

  if (
    contractState?.contract.state === ADD_INFORMATION_STATUS &&
    !onPage([SCREENS.ADD_INFORMATION, SCREENS.THANK_YOU], pathname)
  ) {
    redirect(getContractUrl(uuid, SCREENS.ADD_INFORMATION, isRequest));
  }

  if (
    (contractState?.contract.contractStatus === FOR_REPAIR_STATUS ||
      contractState?.contract.contractStatus === OFFER_ACTIVE_STATUS) &&
    onPage([SCREENS.DOWNLOAD_DOCUMENTS, SCREENS.ADD_INFORMATION, SCREENS.ATTACHMENTS], pathname)
  ) {
    redirect(getContractUrl(uuid, SCREENS.WELCOME, isRequest));
  }

  if (contractState?.contract.state === IS_PROCESSING_STATUS) {
    if (onPage([SCREENS.DOWNLOAD_DOCUMENTS, SCREENS.ADD_INFORMATION], pathname)) {
      redirect(getContractUrl(uuid, SCREENS.WELCOME, isRequest));
    }
    if (
      onPage([SCREENS.ATTACHMENTS], pathname) &&
      additionalDocuments.length === 0 &&
      previousCustomerAdditionalDocuments.length === 0
    ) {
      redirect(getContractUrl(uuid, SCREENS.WELCOME, isRequest));
    }
  }
  if (
    contractState?.contract &&
    contractState.contract.process === Process.PS &&
    isRequest &&
    !onPage([SCREENS.RECAPITULATION], pathname) &&
    isOperatorLink
  ) {
    redirect(getContractUrl(uuid, SCREENS.RECAPITULATION, isRequest), { operator: 1 });
  }

  if (
    contractState?.contract &&
    (isContractCheck(contractState.contract, isRequest) || contractState.contract.gdprAnonymization) &&
    !onPage([SCREENS.RECAPITULATION, SCREENS.THANK_YOU], pathname) &&
    !isNewCustomerLink &&
    !isOperatorLink
  ) {
    redirect(getContractUrl(uuid, SCREENS.RECAPITULATION, isRequest));
  }
};

const onPage = (pages: string[], pathname: string) => {
  return pages.some(path => pathname.includes(path));
};

const addSearchParams = (
  url: string,
  searchParams: ReadonlyURLSearchParams,
  additionalSearchParams?: Record<string, string | number>
) => {
  const [path, existingQuery] = url.split("?");
  const params = new URLSearchParams(existingQuery ?? "");
  Object.entries(additionalSearchParams ?? {}).forEach(([key, value]) => {
    params.set(key, String(value));
  });
  searchParams.forEach((value, key) => {
    if (!params.has(key)) {
      params.append(key, value);
    }
  });
  const query = params.toString();
  return query ? `${path}?${query}` : path ?? url;
};
